import {Component, Input, OnInit, TrackByFunction} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {IncidentTransaction, IncidentType, Participant, ReferenceBookResponse, ReferenceBookType, SubjectType} from '@npck/af-frontend-api';
import {rxSpinner, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import dayjs from 'dayjs';
import {combineLatest, EMPTY, mergeMap, of} from 'rxjs';
import {buildAttachmentsForm} from '../../../../components/files/files.component';
import {PHONE_MIN_LENGTH} from '../../../../components/phone-number/phone-number.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {
    FOREIGN_IBAN_MIN_LENGTH,
    FOREIGN_IBAN_PATTERN,
    IBAN_MAX_LENGTH,
    KZ_IBAN_LENGTH,
    KZ_IBAN_PATTERN,
    MAX_PAYMENT_POINT_NUMBER,
    NUMBER_PATTERN,
    PATTERN_PAYMENT_POINT_NUMBER,
    WALLET_NUMBER_MAX_LENGTH,
    WALLET_NUMBER_MIN_LENGTH,
} from '../../../../domain/constants';
import {DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {createPhoneList, getPhoneInfoByPhoneNumber, kzPhoneInfo, PhoneInfo} from '../../../../domain/phone-utils';
import {INCIDENT_TYPE_CODE} from '../../../../domain/query-params.keys';
import {
    buildLoanInfoForm,
    MAX_RRN_BRN_LENGTH,
    MIN_RRN_BRN_LENGTH,
    requiredFormGroupValidator,
    TransactionForm,
} from './transcation-information/transaction-information.component';
import {ParticipantForm} from './transcation-information/transaction-participant/transaction-participant.component';

@Component({
    selector: 'af-transaction-information-step',
    templateUrl: './transaction-information-step.component.html',
    styleUrls: ['transaction-information-step.component.scss'],
    standalone: false
})
export class TransactionInformationStepComponent implements OnInit {
    @Input() mode: 'create' | 'edit' = 'create';
    @Input() form: FormGroup<TransactionsForm>;
    @Input() indexStartsFrom: number = 0;
    @Input() transactions: Array<IncidentTransaction>;
    isCompromisedPaymentInstrumentType: boolean = false;

    Spinner = Spinner;

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
        private dictionaryHolderService: DictionaryHolderService,
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
    ) {
    }

    get showDeleteButton(): boolean {
        if(!this.authHolderService.hasAccess(['npck-admin'])) {
            return false;
        }
        const transactionsCount =
            (this.form?.controls?.transactions?.controls?.length ?? 0) +
            (this.form?.controls?.addedTransactions?.controls?.length ?? 0);

        return transactionsCount > 1 && !this.Spinner.isShown('initForms');
    }

    ngOnInit(): void {
        if (!this.form.controls.transactions) {
            this.form.addControl('transactions', this.formBuilder.array([] as FormGroup<TransactionForm>[]));
            this.form.addControl('addedTransactions', this.formBuilder.array([] as FormGroup<TransactionForm>[]));
            this.form.addControl('deletedTransactions', this.formBuilder.array([] as FormGroup<TransactionForm>[]));
            if (this.transactions?.length) {
                this.transactions.filter(item => !item.is_deleted).forEach(item => this.createTransactionForm(item));
            } else {
                this.createTransactionForm();
            }
        }
        this.isCompromisedPaymentInstrumentType = this.isCompromisedPaymentInstrumentIncidentType();
    }

    trackByTransactionIndex: TrackByFunction<FormGroup<TransactionForm>> = (index: number) => index;

    createTransactionForm(incident?: IncidentTransaction): void {
        if (incident) {
            this.initializeForm(incident);
        } else {
            this.form.controls.addedTransactions.push(this.formBuilder.group({}));
        }
    }

    deleteTransaction(index: number, isNew: boolean): void {
        const viewIndex =
            isNew ? index + 1 : (this.form.controls.transactions.controls.length - index);
        this.dialogService.openDialog({
            title: isNew ? 'i18n_af_incidentNewTransactionDeletionDialogTitle' : 'i18n_af_incidentTransactionDeletionDialogTitle',
            subtitle: isNew ? 'i18n_af_incidentNewTransactionDeletionDialogSubtitle' : 'i18n_af_incidentTransactionDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {index: `${viewIndex}`},
        })
            .pipe(
                mergeMap(dialogResult => !dialogResult && EMPTY || of(dialogResult)
                    .pipe(rxSpinner(`deleteTransaction_${index}`))),
            )
            .subscribe(() => {
                const rawForm = this.form.getRawValue();
                const deletedTransactionValue =
                    isNew ? rawForm.addedTransactions[index] : rawForm.transactions[index];
                const deletedTransactionControl =
                    isNew ? this.form.controls.addedTransactions?.at(index) : this.form.controls.transactions?.at(index);
                if (!!deletedTransactionValue.id) {
                    deletedTransactionControl.controls.isDeleted.setValue(true);
                    this.transactions.splice(index, 1);
                    this.form.controls.deletedTransactions.push(deletedTransactionControl);
                }
                if (isNew) {
                    this.form.controls.addedTransactions.removeAt(index);
                } else {
                    this.form.controls.transactions.removeAt(index);
                }
            });
    }

    private initializeForm(transaction: IncidentTransaction): void {
        combineLatest(this.dictionaryHolderService.commonDictionaries.filter(dictionary => dictionary !== ReferenceBookType.FRAUD_TYPE)
            .map(referenceBookName => this.dictionaryHolderService.getReference(referenceBookName.toLowerCase())))
            .subscribe(
                ([
                     country,
                     currency,
                     transactionStatus,
                     transactionType,
                     incidentRole,
                     accountStatus,
                     gender,
                     attachedDoc,
                     bin,
                 ]) => {
                    const transactionForm = this.buildTransactionForm(transaction, transactionType, transactionStatus, currency);
                    if (transaction.sender) {
                        transactionForm.addControl(
                            'sender',
                            this.buildParticipantForm(transaction.sender, incidentRole, gender, country, accountStatus, currency, bin),
                        );
                    }
                    transactionForm.addControl(
                        'receiver',
                        this.buildParticipantForm(transaction.receiver, incidentRole, gender, country, accountStatus, currency, bin),
                    );

                    transactionForm.addControl('attachments', buildAttachmentsForm(this.formBuilder, transaction.attachments, attachedDoc));
                    if (transaction.loanInfo) {
                        transactionForm.addControl(
                            'loanInfo',
                            buildLoanInfoForm(this.formBuilder, transaction.loanInfo, currency, attachedDoc),
                        );
                    }
                    this.form.controls.transactions.push(transactionForm);
                });
    }

    private buildTransactionForm(
        transaction: IncidentTransaction,
        transactionType: ReferenceBookResponse[],
        transactionStatus: ReferenceBookResponse[],
        currency: ReferenceBookResponse[],
    ): FormGroup<TransactionForm> {
        const transactionForm = this.formBuilder.group<TransactionForm>({});
        const selectedType = transactionType.find(item => transaction.type === item.name_ru);
        const selectedCurrency = findValueInDictionary(currency, transaction.currency);
        const selectedStatus = findValueInDictionary(transactionStatus, transaction.status);
        transactionForm.addControl('category', new FormControl(selectedType.code1, [Validators.required]));
        transactionForm.addControl('type', new FormControl(selectedType.code, [Validators.required]));
        if (this.isFraudIncidentType()) {
            transactionForm.addControl('fraudScheme', new FormControl(transaction.fraudScheme, [Validators.required]));
            transactionForm.addControl('channel', new FormControl(transaction.channel, [Validators.required]));
        }
        transactionForm.addControl('clientConfirmed', new FormControl(String(transaction.clientConfirmed), [Validators.required]));
        if (transaction.clientConfirmed === true) {
            transactionForm.addControl('confirmationMethod', new FormControl(transaction.confirmationMethod, [Validators.required]));
            transactionForm.addControl(
                'clientLegitimacyConfirmation',
                new FormControl(transaction.clientLegitimacyConfirmation, [Validators.required])
            );
        }
        transactionForm.addControl('transactionDate', this.formBuilder.group({
            date: new FormControl(new Date(transaction.date), [Validators.required]),
            time: new FormControl(dayjs(transaction.date).format('HH:mm'), [Validators.required]),
        }));
        transactionForm.addControl('amount', new FormControl(transaction?.amount));
        transactionForm.addControl('currency', new FormControl(selectedCurrency));
        transactionForm.addControl('rrnBrn', new FormControl(transaction?.transactionRrn, [
            Validators.minLength(MIN_RRN_BRN_LENGTH),
            Validators.maxLength(MAX_RRN_BRN_LENGTH)
        ]));
        transactionForm.addControl('id', new FormControl(transaction?.id));
        transactionForm.addControl('paymentId', new FormControl(transaction?.payment_id));
        transactionForm.addControl('status', new FormControl(selectedStatus, [Validators.required]));
        transactionForm.addControl('isDeleted', new FormControl(null));
        return transactionForm;
    }

    private buildParticipantForm(
        participant: Participant,
        incidentRoleDictionary: ReferenceBookResponse[],
        genderDictionary: ReferenceBookResponse[],
        countryDictionary: ReferenceBookResponse[],
        accountStatusDictionary: ReferenceBookResponse[],
        currencyDictionary: ReferenceBookResponse[],
        binDictionary: ReferenceBookResponse[],
    ): FormGroup<ParticipantForm> {
        const date = participant?.info?.origin_date && new Date(participant?.info.origin_date);
        const participantForm = this.formBuilder.group<ParticipantForm>({}, {validators: requiredFormGroupValidator()});
        const incidentRole = findValueInDictionary(incidentRoleDictionary, participant.incident_role.toLowerCase());
        const gender = participant.info.gender ? findValueInDictionary(genderDictionary, participant.info.gender) : null;
        const country = findValueInDictionary(countryDictionary, participant.country);
        const accountStatus = participant.account?.status ?
            findValueInDictionary(accountStatusDictionary, participant.account?.status) : participant.account?.status;
        const currency = participant.account?.currency ?
            findValueInDictionary(currencyDictionary, participant.account?.currency) : participant.account?.currency;

        if (participant.type === SubjectType.PERSON) {
            participantForm.addControl('name', new FormControl(participant.info.name));
            participantForm.addControl('surname', new FormControl(participant.info.surname));
            participantForm.addControl('patronymic', new FormControl(participant.info.patronymic));
            participantForm.addControl('gender', new FormControl(gender));
            participantForm.addControl('iin', new FormControl(participant.identification_number));
        } else {
            participantForm.addControl('bin', new FormControl(participant.identification_number));
            participantForm.addControl('organizationName', new FormControl(participant.info.name));
        }

        participantForm.addControl('accountId', new FormControl(participant.account?.id ?? null));
        participantForm.addControl('foreignAccountId', new FormControl(participant.foreignAccount?.id ?? null));
        if (participant.incident_role.toLowerCase() === 'suspect') {
            participantForm.addControl('accountStatus', new FormControl(accountStatus));
            participantForm.addControl('accountBalance', new FormControl(participant.account.balance));
            participantForm.addControl('accountCurrency', new FormControl(currency));
        }
        participantForm.addControl('id', new FormControl(participant.id));
        participantForm.addControl('subjectType', new FormControl(participant.type));
        participantForm.addControl('role', new FormControl(incidentRole));
        participantForm.addControl('originDate', this.formBuilder.group({
            date: new FormControl(date),
        }));
        participantForm.addControl('country', new FormControl(country));

        participantForm.addControl('phoneNumberId', new FormControl(participant.phone_number.id));
        participantForm.addControl('phoneNumber', new FormControl(participant.phone_number.number));
        let phoneInfo: PhoneInfo;
        if (!!participant.phone_number?.number && participant?.phone_number.number?.length >= PHONE_MIN_LENGTH) {
            phoneInfo = getPhoneInfoByPhoneNumber(participant.phone_number.number, createPhoneList(countryDictionary));
        }
        participantForm.addControl('selectedPhoneInfo', new FormControl(phoneInfo || kzPhoneInfo));

        participantForm.addControl('cardMaskNumberId', new FormControl(participant.card_mask.id));
        participantForm.addControl('cardMaskNumber', new FormControl(participant.card_mask.card_number));

        participantForm.addControl('requisiteType', new FormControl(participant?.requisiteType ?? null));

        participantForm.addControl('cardIssuerBin', new FormControl(participant?.card_mask?.issuer_bin ?? null));

        participantForm.addControl('accountIssuerBin', new FormControl(participant?.account?.issuerBin ?? null));

        participantForm.addControl('walletNumber', new FormControl(participant?.wallet?.number ?? null, [
            Validators.minLength(WALLET_NUMBER_MIN_LENGTH),
            Validators.maxLength(WALLET_NUMBER_MAX_LENGTH),
            Validators.pattern(NUMBER_PATTERN),
        ]));

        participantForm.addControl('walletIssuerBin', new FormControl(participant?.wallet?.issuer_bin ?? null));

        participantForm.addControl('foreignIban', new FormControl(participant?.foreignAccount?.iban ?? null, [
            Validators.minLength(FOREIGN_IBAN_MIN_LENGTH),
            Validators.maxLength(IBAN_MAX_LENGTH),
            Validators.pattern(FOREIGN_IBAN_PATTERN),
        ]));
        participantForm.addControl('iban', new FormControl(participant?.account?.iban ?? null, [
            Validators.minLength(KZ_IBAN_LENGTH),
            Validators.maxLength(KZ_IBAN_LENGTH),
            Validators.pattern(KZ_IBAN_PATTERN),
        ]));
        participantForm.addControl('foreignIssuerBin', new FormControl(participant?.foreignAccount?.issuerBin ?? null));
        const issuerBin = participant?.paymentPoint?.issuer_bin ?? null;
        participantForm.addControl('paymentPointIssuerBin', new FormControl(issuerBin ?? null));
        participantForm.addControl('paymentPointType', new FormControl(null));
        if (issuerBin) {
            const bin = findValueInDictionary(binDictionary, issuerBin);
            const organizationType = findValueInDictionary(binDictionary, issuerBin, 'code1');
            if (bin) {
                participantForm.controls.paymentPointType.setValue(organizationType);
                participantForm.controls.paymentPointIssuerBin.setValue(bin);
            }
        }
        participantForm.addControl('paymentPointNumber', new FormControl(participant?.paymentPoint?.number ?? null, [
            Validators.maxLength(MAX_PAYMENT_POINT_NUMBER),
            Validators.pattern(PATTERN_PAYMENT_POINT_NUMBER),
        ]));
        participantForm.addControl('paymentPointAddress', new FormControl(participant?.paymentPoint?.address ?? null));
        participantForm.addControl('walletId', new FormControl(participant?.wallet?.id ?? null));
        participantForm.addControl('paymentPointId', new FormControl(participant?.paymentPoint?.id ?? null));
        return participantForm;
    }

    private isCompromisedPaymentInstrumentIncidentType(): boolean {
        return (
            this.form.root?.get('commonInformationForm.type')?.value ??
            this.activatedRoute.snapshot.queryParamMap.get(INCIDENT_TYPE_CODE)
        ) === IncidentType.COMPROMISED_PAYMENT_INSTRUMENT;
    }

    private isFraudIncidentType(): boolean {
        return (
            this.form.root?.get('commonInformationForm.type')?.value ??
            this.activatedRoute.snapshot.queryParamMap.get(INCIDENT_TYPE_CODE)
        ) === IncidentType.FRAUD;
    }
}

export type TransactionsFormValue = ɵTypedOrUntyped<TransactionsForm, ɵFormGroupRawValue<TransactionsForm>, any>;

export interface TransactionsForm {
    transactions?: FormArray<FormGroup<TransactionForm>>;
    addedTransactions?: FormArray<FormGroup<TransactionForm>>;
    deletedTransactions?: FormArray<FormGroup<TransactionForm>>;
}
